// Dialog for adding a new category node.
// Collects name, optional description, and owner role.
// Owner role dropdown loaded from the ownership roles API.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { ownershipRolesApi } from '@/lib/api/ownershipRoles'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'

interface AddCategoryDialogProps {
  open: boolean
  parentName: string
  onConfirm: (name: string, description: string, ownerRoleId: string) => void
  onCancel: () => void
  isPending?: boolean
}

export function AddCategoryDialog({
  open,
  parentName,
  onConfirm,
  onCancel,
  isPending,
}: AddCategoryDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [ownerRoleId, setOwnerRoleId] = useState('')

  const rolesQuery = useQuery({
    queryKey: ['tenant', slug, 'ownership-roles'],
    queryFn: () => ownershipRolesApi(api).list(),
    enabled: open,
  })

  const roles = rolesQuery.data ?? []
  const canSubmit = name.trim() && ownerRoleId && !isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (canSubmit) {
      onConfirm(name.trim(), description.trim(), ownerRoleId)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel()
      resetForm()
    }
  }

  function resetForm() {
    setName('')
    setDescription('')
    setOwnerRoleId('')
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add child under &ldquo;{parentName}&rdquo;</DialogTitle>
          <DialogDescription>
            This category will be created immediately as a child of the selected node.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Name</Label>
              <Input
                id="cat-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Network Security"
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-description">Description (optional)</Label>
              <Input
                id="cat-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this category"
                maxLength={1000}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-owner-role">Owner Role</Label>
              {rolesQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading roles...
                </div>
              ) : (
                <select
                  id="cat-owner-role"
                  value={ownerRoleId}
                  onChange={(e) => setOwnerRoleId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select an ownership role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                      {role.owner_display_name ? ` (${role.owner_display_name})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Add Category'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
